import { useEffect, useState } from 'react'
import { useAppDispatch } from '../../reducers/store'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import PageSkeleton from '../skeleton/PageSkeleton.tsx'
import { selectUser } from '../../reducers/users/users.slice.ts'
import { inPov } from '../../utils/session.ts'
import {
  HEADER_HEIGHT_WITH_TRIAL,
  HEADER_HEIGHT_WITHOUT_TRIAL,
  TABLE_HEIGHT_WITH_TRIAL,
  TABLE_HEIGHT_WITH_TRIAL_AND_SCIM,
  TABLE_HEIGHT_WITHOUT_TRIAL,
  TABLE_HEIGHT_WITHOUT_TRIAL_AND_SCIM,
} from '../../strings/consts.ts'
import { getUserGroups } from '../../reducers/groups/groups.thunk.ts'
import { selectGroup, setUserGroups } from '../../reducers/groups/groups.slice.ts'
import DataGrid from '../data-grid/DataGrid.tsx'
import { groupColumns } from './GroupColumns.tsx'
import { IconShieldCheckFilled, IconUsersGroup } from '@tabler/icons-react'
import { selectIntegrations } from '../../reducers/integrations/integrations.slice.ts'
import { fetchIntegrationsList } from '../../reducers/integrations/integrations.thunk.ts'
import { IntegrationDetails } from '../../types/IntegrationTypes.ts'
import { Button } from '@radix-ui/themes'
import ConfigureScimImage from '../../assets/configure-scim.png'

function GroupsWrapper() {
  const { userGroups } = useSelector(selectGroup)
  const { integrationsList } = useSelector(selectIntegrations)
  const di = useAppDispatch()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const [dataLoading, setDataLoading] = useState<boolean>(true)
  const [scimIntegration, setScimIntegration] = useState<boolean>(true)
  const [scimProviderName, setScimProviderName] = useState<string>('')

  const { session } = useSelector(selectUser)

  useEffect(() => {
    if (userGroups) {
      setDataLoading(false)
    } else if (!userGroups) {
      setDataLoading(true)
    }
  }, [userGroups])

  useEffect(() => {
    const session = localStorage.getItem('session') as string

    if (session === '{}') {
      navigate('/login')
    }
    di(getUserGroups())

    return () => {
      dispatch(setUserGroups(undefined))
    }
  }, [])

  useEffect(() => {
    di(fetchIntegrationsList())
  }, [])

  useEffect(() => {
    if (integrationsList) {
      const activePage = integrationsList?.find((item) => item.pageState === 'ACTIVE')
      const scimIntegration: IntegrationDetails | undefined = activePage
        ? activePage.integrations.find((i: IntegrationDetails) => i.category === 'SCIM')
        : undefined

      if (scimIntegration) {
        setScimIntegration(true)
        setScimProviderName(scimIntegration.name)
      } else {
        setScimIntegration(false)
        setScimProviderName('')
      }
    }
  }, [integrationsList])

  const navigateToGroup = (groupId: number) => {
    navigate(`/groups/${groupId}`)
  }

  return (
    <>
      <div
        className="absolute top-18 left-52 rounded-tl-lg border-mainBorder border-t border-l w-[calc(100%_-_208px)] bg-contentBackground p-9"
        style={{
          height: inPov(session)
            ? `calc(100% - ${HEADER_HEIGHT_WITH_TRIAL})`
            : `calc(100% - ${HEADER_HEIGHT_WITHOUT_TRIAL})`,
        }}
      >
        {dataLoading && (
          <div className="flex justify-center items-center min-h-screen">
            <PageSkeleton />
          </div>
        )}

        {!dataLoading && scimIntegration && (
          <div className="w-full px-4 py-3 bg-tableRowGrey rounded-md outline outline-1 outline-offset-[-1px] outline-mainBorder inline-flex justify-between items-center mb-2">
            <div className="inline-flex flex-col justify-start items-start gap-1">
              <div className="inline-flex justify-start items-center gap-2">
                <div className="w-2 h-2 bg-successGreen rounded-[50px] border-[0.67px] border-green-300"></div>
                <div className="justify-center text-mainText text-sm leading-4 font-medium">
                  SCIM Provider Configured
                </div>
              </div>
              <div className="justify-center text-text-Neutral-Secondary text-xs">
                All groups and devices are managed from {scimProviderName}
              </div>
            </div>
            <div className="w-80 flex justify-end items-center gap-2">
              <div
                className="h-7 px-2 rounded flex justify-center items-center gap-1 overflow-hidden cursor-pointer"
                onClick={() =>
                  (window.location.href = `${window.location.origin}/docs/iverify-portal-guide/Integrations/`)
                }
              >
                <div className="justify-center text-mainText text-xs font-medium leading-4">Read Docs</div>
              </div>
              <div
                className="h-7 px-2 bg-button-secondary-background rounded outline outline-1 outline-offset-[-1px] outline-mainBorder flex justify-center items-center gap-1 overflow-hidden cursor-pointer"
                onClick={() => navigate('/integrations')}
              >
                <div className="justify-center text-mainText text-xs font-medium leading-4">Manage</div>
              </div>
            </div>
          </div>
        )}

        {userGroups && userGroups.length > 0 && (
          <>
            <DataGrid
              columns={groupColumns(navigateToGroup)}
              data={userGroups}
              loading={dataLoading}
              classNames={
                inPov(session)
                  ? `calc(100% - ${scimIntegration ? TABLE_HEIGHT_WITH_TRIAL_AND_SCIM : TABLE_HEIGHT_WITH_TRIAL})`
                  : `calc(100% - ${scimIntegration ? TABLE_HEIGHT_WITHOUT_TRIAL_AND_SCIM : TABLE_HEIGHT_WITHOUT_TRIAL})`
              }
            />
          </>
        )}
        {scimIntegration && userGroups && userGroups.length === 0 && (
          <div className="block items-center mt-20">
            <div className="flex items-center justify-center">
              <IconUsersGroup className="h-12 w-12" />
            </div>
            <p className="text-center font-bold mt-4">No groups found</p>
            <p className="text-center text-sm flex mx-auto max-w-[300px]">
              Assign groups to your application within your IdP
            </p>
            <Button
              className="h-[34px] flex text-mainText bg-white hover:bg-white active:active:bg-white border-mainBorder mx-auto cursor-pointer mt-4"
              onClick={() => navigate('/integrations')}
            >
              Manage Configuration
            </Button>
          </div>
        )}
        {!scimIntegration && userGroups && userGroups.length === 0 && (
          <div className="flex items-center mt-10">
            <div className="w-[800px] px-6 bg-white rounded-lg inline-flex justify-start items-center gap-8 mx-auto">
              <div className="w-72 self-stretch px-6 py-14 inline-flex flex-col justify-center items-start gap-2 overflow-hidden">
                <img src={ConfigureScimImage} />
              </div>
              <div className="py-8 inline-flex flex-col justify-center items-start gap-6">
                <div className="w-96 flex flex-col justify-start items-start gap-4">
                  <div className="self-stretch flex flex-col justify-start items-start gap-2">
                    <div className="self-stretch justify-center text-black text-2xl font-semibold leading-8">
                      Configure SCIM to sync
                      <br />
                      groups from your IdP
                    </div>
                    <div className="w-72 justify-center text-black text-xs font-medium leading-4">
                      Manage members from one place and control access to iVerify Enterprise at scale
                    </div>
                  </div>
                  <div className="flex flex-col justify-start items-start gap-2">
                    <div className="inline-flex justify-start items-center gap-2">
                      <div className="relative overflow-hidden">
                        <IconShieldCheckFilled size={18} className="text-neutral-9" />
                      </div>
                      <div className="text-center justify-center text-black text-xs font-normal leading-4">
                        Sync groups automatically from your IdP
                      </div>
                    </div>
                    <div className="inline-flex justify-start items-center gap-2">
                      <div className="relative overflow-hidden">
                        <IconShieldCheckFilled size={18} className="text-neutral-9" />
                      </div>
                      <div className="text-center justify-center text-black text-xs font-normal leading-4">
                        Assign groups to policies for flexible access control
                      </div>
                    </div>
                    <div className="inline-flex justify-start items-center gap-2">
                      <div className="relative overflow-hidden">
                        <IconShieldCheckFilled size={18} className="text-neutral-9" />
                      </div>
                      <div className="text-center justify-center text-black text-xs font-normal leading-4">
                        Support overlapping and mixed access needs
                      </div>
                    </div>
                  </div>
                </div>
                <div className="flex flex-col justify-start items-start gap-3">
                  <div className="inline-flex justify-start items-start gap-2">
                    <div
                      className="flex justify-start items-start gap-2 cursor-pointer"
                      onClick={() => navigate('/integrations')}
                    >
                      <div className="h-7 px-2 bg-PurpleBlue-11 rounded flex justify-center items-center gap-1 overflow-hidden">
                        <div className="justify-center text-white text-xs font-medium leading-4">Configure SCIM</div>
                      </div>
                    </div>
                    <div
                      className="flex justify-start items-start gap-2 cursor-pointer"
                      onClick={() =>
                        window.open(`${window.location.origin}/docs/iverify-portal-guide/Integrations/`, '_blank')
                      }
                    >
                      <div className="h-7 px-2 bg-surfaceSecondary rounded outline outline-1 outline-offset-[-1px] outline-mainBorder flex justify-center items-center gap-1 overflow-hidden">
                        <div className="justify-center text-textSecondary text-xs font-medium leading-4">
                          View SCIM Docs
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="justify-center text-text-Neutral-Tertiary text-xs font-normal leading-4">
                    Note: A role of Platform Management or Super Admin is required to configure integrations
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  )
}

export default GroupsWrapper
